import { useClipboard } from '@pesto/ui';
import { Typography } from '@pesto/ui';
import { Badge } from '@pesto/ui/components/Badge';
import { Card } from '@pesto/ui/components/Card';
import { CompactField } from '@pesto/ui/components/CompactField';
import { ScrollArea } from '@pesto/ui/components/ScrollArea';
import { Separator } from '@pesto/ui/components/Separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@pesto/ui/components/Tooltip';
import { centsToFormattedDollars, formatDate } from '@pesto/utils';
import { Clock, Copy, ExternalLink, FileText, Landmark } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { StatementListItem } from '../types/Statements.types';

interface StatementDetailsCardProps {
  statement: StatementListItem;
  getLoanHref?: (loanId: string) => string;
}

const formatText = (value: string | null | undefined): string => {
  if (!value) return '—';

  if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
    const isDateOnly = !value.includes('T');

    return formatDate(value, isDateOnly ? 'd MMMM yyyy' : undefined) ?? value;
  }

  return value;
};

const formatCents = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';

  return centsToFormattedDollars(value) ?? String(value);
};

const formatYesNo = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return '—';

  return value ? 'Yes' : 'No';
};

const HeroBox = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-center justify-center p-4">
    <Typography variant="body" className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
      {label}
    </Typography>
    <Typography variant="headerSmall" className="mt-1 text-xl font-bold">
      {value}
    </Typography>
  </div>
);

const SummaryRow = ({ label, value, operator }: { label: string; value: string; operator?: string }) => (
  <div className="flex items-center justify-between border-b border-dashed py-2 last:border-b-0">
    <span className="text-sm">
      {operator && <span className="text-muted-foreground mr-1.5 inline-block w-3">{operator}</span>}
      {label}
    </span>
    <span className="text-sm tabular-nums">{value}</span>
  </div>
);

const AccentRow = ({ label, value }: { label: string; value: string }) => (
  <div className="border-primary/40 flex items-center justify-between border-l-2 py-2 pl-3">
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className="text-sm tabular-nums">{value}</span>
  </div>
);

export const StatementDetailsCard = ({ statement, getLoanHref }: StatementDetailsCardProps) => {
  const { copy } = useClipboard();
  const year = statement.billingPeriodEnd.slice(0, 4);

  const heroItems = [
    { label: 'Statement Balance', value: formatText(statement.newBalance) },
    { label: 'Payment Due Date', value: formatText(statement.dueDate) },
    { label: 'Minimum Payment Due', value: formatText(statement.minimumPaymentDue) },
  ];

  const summaryRows = [
    { label: 'Previous Balance', value: formatText(statement.previousBalance) },
    { operator: '−', label: 'Payments', value: formatText(statement.totalPayments) },
    { operator: '−', label: 'Other Credits', value: formatText(statement.otherCredits) },
    { operator: '+', label: 'Purchase Transactions', value: formatText(statement.totalPurchases) },
    { operator: '+', label: 'Cash Disbursements', value: formatText(statement.cashAdvance) },
    { operator: '+', label: 'Interest Charged', value: formatText(statement.interestCharged) },
    { operator: '+', label: 'Fee(s) Charged', value: formatText(statement.feesCharged) },
  ];

  const accentRows = [
    { label: 'Credit Limit', value: formatText(statement.creditLimit) },
    { label: 'Credit Limit Available', value: formatText(statement.creditLimitAvailable) },
  ];

  const ytdItems = [
    { label: `Total Fees Charged in ${year}`, value: formatText(statement.ytdFeesCharged) },
    { label: `Total Interest Charged in ${year}`, value: formatText(statement.ytdInterestCharged) },
  ];

  const dateFields = [
    { label: 'Period Start', value: formatText(statement.billingPeriodStart) },
    { label: 'Period End', value: formatText(statement.billingPeriodEnd) },
    { label: 'Statement Date', value: formatText(statement.statementDate) },
  ];

  const additionalDetails = [
    { label: 'Closing Balance', value: formatText(statement.closingBalance) },
    { label: 'Avg Daily Balance', value: formatCents(statement.averageDailyBalance) },
    { label: 'Grace Savings', value: formatCents(statement.graceSavings) },
    { label: 'Grace Active', value: formatYesNo(statement.graceActive) },
    { label: 'Paid in Full', value: formatYesNo(statement.previousBalancePaidInFull) },
    { label: 'Interest Applied', value: formatText(statement.interestApplied) },
    { label: 'Late Fee Applied', value: formatText(statement.lateFeeApplied) },
  ];

  const periodEndBalances = [
    { label: 'Purchase', value: formatCents(statement.purchaseBalanceAtPeriodEnd) },
    { label: 'Drawdown', value: formatCents(statement.drawdownBalanceAtPeriodEnd) },
    { label: 'Interest', value: formatCents(statement.interestBalanceAtPeriodEnd) },
    { label: 'Fee', value: formatCents(statement.feeBalanceAtPeriodEnd) },
    { label: 'Repayment', value: formatCents(statement.repaymentBalanceAtPeriodEnd) },
    { label: 'New Purchase', value: formatCents(statement.newPurchaseBalanceAtPeriodEnd) },
  ];

  const timestamps = [
    { label: 'Due Date Processed', value: formatText(statement.dueDateProcessedAt) },
    { label: 'Late Fee Processed', value: formatText(statement.lateFeeProcessedAt) },
    { label: 'Autopay Processed', value: formatText(statement.autopayProcessedAt) },
    { label: 'Created', value: formatText(statement.insertedAt) },
    { label: 'Updated', value: formatText(statement.updatedAt) },
  ];

  const loanHref = statement.loanId && getLoanHref ? getLoanHref(statement.loanId) : null;

  return (
    <Card className="space-y-5 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
            <FileText className="text-primary h-4 w-4" />
          </div>
          <div>
            <Typography variant="body" className="text-sm font-semibold">
              Statement
            </Typography>
            <Typography variant="body" className="text-muted-foreground font-mono text-xs">
              {statement.id}
            </Typography>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statement.hasPdf && (
            <Badge variant="success" className="text-xs">
              PDF
            </Badge>
          )}
          {statement.graceActive && (
            <Badge variant="secondary" className="text-xs">
              Grace Active
            </Badge>
          )}
          {statement.previousBalancePaidInFull && (
            <Badge variant="default" className="text-xs">
              Paid in Full
            </Badge>
          )}
          {loanHref && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={loanHref}
                  className="bg-muted hover:bg-muted/80 flex h-7 w-7 items-center justify-center rounded-md transition-colors"
                >
                  <Landmark className="h-3.5 w-3.5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>View loan{statement.loanPartnerAccountNumber ? ` (${statement.loanPartnerAccountNumber})` : ''}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1">
        {dateFields.map((field) => (
          <CompactField key={field.label} label={field.label} value={field.value} />
        ))}
      </div>

      <div className="divide-x rounded-lg border">
        <div className="grid grid-cols-3 divide-x">
          {heroItems.map((item) => (
            <HeroBox key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
      </div>

      <div className="rounded-lg border">
        <div className="border-b px-4 py-2.5">
          <Typography variant="body" className="text-xs font-semibold uppercase tracking-wide">
            Account Summary
          </Typography>
        </div>
        <div className="px-4 py-2">
          {summaryRows.map((row) => (
            <SummaryRow key={row.label} label={row.label} value={row.value} operator={row.operator} />
          ))}
          <Separator className="my-1" />
          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-bold">Statement Balance</span>
            <span className="text-sm font-bold tabular-nums">{formatText(statement.newBalance)}</span>
          </div>
          <Separator className="my-1" />
          <div className="mt-1 space-y-0">
            {accentRows.map((row) => (
              <AccentRow key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {ytdItems.map((item) => (
          <div key={item.label} className="rounded-lg border">
            <HeroBox label={item.label} value={item.value} />
          </div>
        ))}
      </div>

      <div className="rounded-lg border">
        <div className="border-b px-4 py-2.5">
          <Typography variant="body" className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
            Additional Details
          </Typography>
        </div>
        <div className="space-y-4 px-4 py-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {additionalDetails.map((field) => (
              <CompactField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
          <Separator />
          <div>
            <Typography
              variant="body"
              className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide"
            >
              Period End Balances
            </Typography>
            <div className="grid grid-cols-3 gap-x-6 gap-y-2">
              {periodEndBalances.map((field) => (
                <CompactField key={field.label} label={field.label} value={field.value} />
              ))}
            </div>
          </div>
          <Separator />
          <div>
            <Typography
              variant="body"
              className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide"
            >
              PDF
            </Typography>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              {statement.pdfFilename && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground shrink-0">File:</span>
                  <ScrollArea orientation="horizontal" className="max-w-md">
                    <a
                      href={statement.pdfFilename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary whitespace-nowrap font-medium hover:underline"
                    >
                      {statement.pdfFilename}
                    </a>
                  </ScrollArea>
                  <a
                    href={statement.pdfFilename}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1 transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <button
                    onClick={() => void copy(statement.pdfFilename!)}
                    className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <Separator />
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Clock className="text-muted-foreground h-3.5 w-3.5" />
              <Typography variant="body" className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Timestamps
              </Typography>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {timestamps.map((ts) => (
                <span key={ts.label} className="text-muted-foreground">
                  {ts.label}: <span className="text-foreground font-medium">{ts.value}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
